import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  approveGumrukAgcEntry,
  approveGumrukAgcRegistrationWarning,
  finishGumrukAgcFlow,
  getGumrukAgcCaptcha,
  getGumrukAgcEntryStatus,
  prepareGumrukAgcApproval,
  rejectGumrukAgcEntry,
} from "../../../features/gumrukAgc/gumrukAgcSlice";
import { showModal } from "../../../features/modal/modalSlice";
import { restoreGumrukAssignmentContext } from "../../../features/joblist/jobListSlice";
import { useScrollLock } from "../../../hooks/useScrollLock";
import Btn from "../../shared/button/Btn";
import ResultSuccessTickMark from "../../shared/ResultSuccessTickMark";

const PROCESSING_STATUSES = new Set([
  "Approved",
  "Validating",
  "DraftCreating",
  "DraftCreated",
  "AddingTruck",
  "AddingTrailer",
  "AddingContainer",
  "CheckingWarnings",
  "ReadyToRegister",
  "Registering",
]);

const STATUS_TEXT = {
  Approved: "Gümrük bildirimi sıraya alındı.",
  Validating: "Bilgiler doğrulanıyor.",
  DraftCreating: "Gümrük taslağı oluşturuluyor.",
  DraftCreated: "Gümrük taslağı oluşturuldu.",
  AddingTruck: "Çekici bilgisi ekleniyor.",
  AddingTrailer: "Dorse bilgisi ekleniyor.",
  AddingContainer: "Konteyner bilgisi ekleniyor.",
  CheckingWarnings: "GTB uyarıları kontrol ediliyor.",
  ReadyToRegister: "Tescil için hazırlanıyor.",
  Registering: "Gümrük bildirimi tescil ediliyor.",
};

const ModalContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 35;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.62);

  .modal {
    width: min(620px, 100%);
    max-height: calc(100vh - 40px);
    overflow-y: auto;
    padding: 36px;
    border-radius: 12px;
    color: ${({ theme }) => theme.mainTextColor};
    background: ${({ theme }) => theme.mainBgColor};
  }

  h2 { margin: 0 0 24px; text-align: center; font-size: 22px; }
  p { margin: 0; line-height: 1.5; text-align: center; }
  .fields { display: grid; gap: 12px; margin: 22px 0; }
  .field { display: grid; grid-template-columns: 150px 1fr; gap: 16px; padding: 12px 14px; border-radius: 8px; background: rgba(128, 128, 128, 0.1); }
  .label { font-weight: 700; }
  .captcha { display: flex; flex-direction: column; align-items: center; gap: 12px; margin: 20px 0; }
  .captcha img { max-width: 100%; min-height: 64px; border-radius: 6px; background: #fff; object-fit: contain; }
  .captcha input { width: min(300px, 100%); box-sizing: border-box; padding: 11px 12px; border: 1px solid #aaa; border-radius: 8px; font-size: 16px; }
  .countdown { font-weight: 700; color: var(--color-main); }
  .actions { display: flex; justify-content: center; flex-wrap: wrap; gap: 12px; margin-top: 24px; }
  .center { display: flex; flex-direction: column; align-items: center; gap: 16px; }
  .spinner { width: 42px; height: 42px; border: 4px solid rgba(128, 128, 128, 0.25); border-top-color: var(--color-main); border-radius: 50%; animation: spin 0.8s linear infinite; }
  .error { margin-top: 16px; color: #d6453d; }
  .warning { padding: 14px; border-radius: 8px; background: rgba(232, 84, 70, 0.1); white-space: pre-wrap; }
  .iagc { font-size: 18px; font-weight: 700; }
  @keyframes spin { to { transform: rotate(360deg); } }

  @media (max-width: 600px) {
    .modal { padding: 26px 20px; }
    .field { grid-template-columns: 1fr; gap: 5px; }
    .actions button { width: 100%; }
  }
`;

const formatRemaining = (seconds) => {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
  const remainder = (seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${remainder}`;
};

const GumrukAgcModal = () => {
  const dispatch = useDispatch();
  const { lockScroll, unlockScroll } = useScrollLock();
  const state = useSelector((store) => store.gumrukAgc);
  const {
    operationId,
    operation,
    assignmentContext,
    captchaRequired,
    sessionAvailable,
    challengeId,
    challengeExpiresAt,
    isPreparing,
    isLoadingCaptcha,
    isApproving,
    isRejecting,
    isCheckingStatus,
    isApprovingWarning,
    errorMessage,
  } = state;
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaUrl, setCaptchaUrl] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const preparedStatusRef = useRef(null);
  const refreshedChallengeRef = useRef(null);
  const initialStatusRequestedRef = useRef(null);
  const captchaRequestRef = useRef({ challengeId: null, promise: null });

  const status = operation?.status ?? null;
  const isBusy = isPreparing || isApproving || isRejecting || isApprovingWarning;

  useEffect(() => {
    lockScroll();
    return unlockScroll;
  }, [lockScroll, unlockScroll]);

  useEffect(() => {
    const preventEscape = (event) => {
      if (event.key === "Escape") event.preventDefault();
    };
    document.addEventListener("keydown", preventEscape);
    return () => document.removeEventListener("keydown", preventEscape);
  }, []);

  useEffect(() => {
    if (
      operationId &&
      !operation &&
      !isCheckingStatus &&
      initialStatusRequestedRef.current !== operationId
    ) {
      initialStatusRequestedRef.current = operationId;
      dispatch(getGumrukAgcEntryStatus(operationId));
    }
  }, [dispatch, isCheckingStatus, operation, operationId]);

  useEffect(() => {
    const shouldPrepare = status === "AwaitingUserApproval" || status === "SessionRequired";
    const prepareKey = shouldPrepare ? `${operationId}:${status}` : null;
    if (!prepareKey || preparedStatusRef.current === prepareKey || isPreparing) return;

    preparedStatusRef.current = prepareKey;
    setCaptchaAnswer("");
    dispatch(prepareGumrukAgcApproval(operationId));
  }, [dispatch, isPreparing, operationId, status]);

  useEffect(() => {
    if (!PROCESSING_STATUSES.has(status) || isCheckingStatus) return undefined;
    const timer = window.setTimeout(() => {
      dispatch(getGumrukAgcEntryStatus(operationId));
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [dispatch, isCheckingStatus, operationId, status]);

  useEffect(() => {
    if (!captchaRequired || !challengeId) {
      captchaRequestRef.current = { challengeId: null, promise: null };
      setCaptchaUrl((currentUrl) => {
        if (currentUrl) URL.revokeObjectURL(currentUrl);
        return null;
      });
      return undefined;
    }

    if (captchaRequestRef.current.challengeId !== challengeId) {
      captchaRequestRef.current = {
        challengeId,
        promise: dispatch(getGumrukAgcCaptcha(challengeId)).unwrap(),
      };
    }

    let subscribed = true;
    captchaRequestRef.current.promise
      .then(({ blob }) => {
        if (!subscribed) return;
        const nextUrl = URL.createObjectURL(blob);
        setCaptchaUrl((currentUrl) => {
          if (currentUrl) URL.revokeObjectURL(currentUrl);
          return nextUrl;
        });
      })
      .catch(() => {
        if (captchaRequestRef.current.challengeId === challengeId) {
          captchaRequestRef.current = { challengeId: null, promise: null };
        }
      });

    return () => {
      subscribed = false;
    };
  }, [challengeId, captchaRequired, dispatch]);

  useEffect(() => () => {
    if (captchaUrl) URL.revokeObjectURL(captchaUrl);
  }, [captchaUrl]);

  useEffect(() => {
    if (!challengeExpiresAt) {
      setRemainingSeconds(0);
      return undefined;
    }

    const updateRemaining = () => {
      setRemainingSeconds(Math.max(0, Math.ceil((challengeExpiresAt - Date.now()) / 1000)));
    };
    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [challengeExpiresAt]);

  useEffect(() => {
    if (
      !captchaRequired ||
      !challengeId ||
      !challengeExpiresAt ||
      Date.now() < challengeExpiresAt ||
      remainingSeconds > 0 ||
      isPreparing ||
      refreshedChallengeRef.current === challengeId
    ) return;

    refreshedChallengeRef.current = challengeId;
    setCaptchaAnswer("");
    dispatch(prepareGumrukAgcApproval(operationId));
  }, [captchaRequired, challengeExpiresAt, challengeId, dispatch, isPreparing, operationId, remainingSeconds]);

  const handleApprove = useCallback(async () => {
    if (isBusy || (captchaRequired && (!captchaAnswer.trim() || remainingSeconds === 0))) return;
    try {
      await dispatch(approveGumrukAgcEntry({
        operationId,
        challengeId: captchaRequired ? challengeId : null,
        captchaAnswer: captchaRequired ? captchaAnswer.trim() : null,
      })).unwrap();
      setCaptchaAnswer("");
      dispatch(getGumrukAgcEntryStatus(operationId));
    } catch {
      setCaptchaAnswer("");
      dispatch(prepareGumrukAgcApproval(operationId));
    }
  }, [captchaAnswer, captchaRequired, challengeId, dispatch, isBusy, operationId, remainingSeconds]);

  const handleReject = useCallback(async () => {
    if (status !== "AwaitingUserApproval" || isBusy) return;
    try {
      await dispatch(rejectGumrukAgcEntry(operationId)).unwrap();
      toast.info("Gümrük giriş bildirimi yapılmadı.");
      if (assignmentContext) dispatch(restoreGumrukAssignmentContext(assignmentContext));
      dispatch(finishGumrukAgcFlow());
      dispatch(showModal("jobListBirebirEslestirResult"));
    } catch {}
  }, [assignmentContext, dispatch, isBusy, operationId, status]);

  const handleWarningApproval = useCallback(async () => {
    if (isBusy) return;
    try {
      await dispatch(approveGumrukAgcRegistrationWarning(operationId)).unwrap();
      dispatch(getGumrukAgcEntryStatus(operationId));
    } catch {}
  }, [dispatch, isBusy, operationId]);

  const continueToAssignmentResult = useCallback(() => {
    if (assignmentContext) dispatch(restoreGumrukAssignmentContext(assignmentContext));
    dispatch(finishGumrukAgcFlow());
    dispatch(showModal("jobListBirebirEslestirResult"));
  }, [assignmentContext, dispatch]);

  const vehicleFields = useMemo(() => [
    ["Çekici", operation?.truckPlate],
    ["Dorse", operation?.trailerPlate],
    ["Konteyner", operation?.containerNumber],
  ], [operation]);

  const renderContent = () => {
    if (!operation && errorMessage) {
      return (
        <div className="center">
          <p>Bekleyen gümrük işlemi yüklenemedi.</p>
          <Btn
            width="210px"
            color="flex"
            isDisabled={isCheckingStatus}
            handleClick={() => dispatch(getGumrukAgcEntryStatus(operationId))}
          >
            {isCheckingStatus ? "Kontrol ediliyor..." : "Tekrar dene"}
          </Btn>
        </div>
      );
    }

    if (!operation || isPreparing) {
      return <div className="center"><div className="spinner"/><p>Gümrük onayı hazırlanıyor...</p></div>;
    }

    if (status === "Registered") {
      return (
        <div className="center">
          <ResultSuccessTickMark />
          <p>Serbest bölge giriş bildirimi başarıyla oluşturuldu.</p>
          <p className="iagc">IAGC: {operation.remoteIagc || "-"}</p>
          <Btn width="200px" bgColor="#E85446" handleClick={continueToAssignmentResult}>Devam et</Btn>
        </div>
      );
    }

    if (status === "RequiresRemoteWarningApproval") {
      return (
        <>
          <p>GTB tescil işlemi aşağıdaki uyarı için ek onay bekliyor.</p>
          <p className="warning">{operation.lastRemoteMessage || "GTB bir tescil uyarısı döndürdü."}</p>
          <p>Onaylamazsanız işlem bu durumda kalır; daha sonra aynı operasyondan devam edebilirsiniz.</p>
          <div className="actions">
            <Btn width="220px" bgColor="#E85446" isDisabled={isBusy} handleClick={handleWarningApproval}>
              {isApprovingWarning ? "Onaylanıyor..." : "Uyarıyı onayla"}
            </Btn>
          </div>
        </>
      );
    }

    if (PROCESSING_STATUSES.has(status)) {
      return <div className="center"><div className="spinner"/><p>{STATUS_TEXT[status] || "Gümrük işlemi sürüyor."}</p></div>;
    }

    if (status === "AwaitingUserApproval" || status === "SessionRequired") {
      const approvalIsReady = captchaRequired || sessionAvailable;
      const approveDisabled = !approvalIsReady || isBusy || isLoadingCaptcha ||
        (captchaRequired && (!captchaAnswer.trim() || remainingSeconds === 0));
      return (
        <>
          <p>Gümrük giriş bildirimi için aşağıdaki araç bilgilerini kontrol edin.</p>
          <div className="fields">
            {vehicleFields.map(([label, value]) => (
              <div className="field" key={label}><span className="label">{label}</span><span>{value || "-"}</span></div>
            ))}
          </div>
          {captchaRequired && (
            <div className="captcha">
              {captchaUrl ? <img src={captchaUrl} alt="Gümrük CAPTCHA" /> : <div className="spinner" />}
              <span className="countdown">Kalan süre: {formatRemaining(remainingSeconds)}</span>
              <input
                value={captchaAnswer}
                onChange={(event) => setCaptchaAnswer(event.target.value)}
                disabled={remainingSeconds === 0 || isBusy}
                autoComplete="off"
                aria-label="CAPTCHA cevabı"
                placeholder="Görseldeki kodu girin"
              />
            </div>
          )}
          {sessionAvailable && !captchaRequired && <p>Aktif GTB oturumu bulundu; CAPTCHA gerekmiyor.</p>}
          <div className="actions">
            <Btn width="170px" color="flex" isDisabled={status !== "AwaitingUserApproval" || isBusy} handleClick={handleReject}>
              {isRejecting ? "İşleniyor..." : "Vazgeç"}
            </Btn>
            <Btn width="190px" bgColor="#E85446" isDisabled={approveDisabled} handleClick={handleApprove}>
              {isApproving ? "Bildiriliyor..." : "Gümrüğe bildir"}
            </Btn>
            {!approvalIsReady && !isPreparing && (
              <Btn width="190px" color="flex" isDisabled={isBusy} handleClick={() => dispatch(prepareGumrukAgcApproval(operationId))}>
                Yeniden hazırla
              </Btn>
            )}
          </div>
        </>
      );
    }

    const needsManualCheck = status === "UnknownRemoteState";
    return (
      <div className="center">
        <p>{needsManualCheck
          ? "İşlemin GTB tarafındaki sonucu kesinleşmedi. Otomatik tekrar yapılmayacak; kaydı GTB üzerinden kontrol edin."
          : "Gümrük bildirimi tamamlanamadı."}</p>
        {(operation.lastErrorMessage || operation.lastRemoteMessage) && (
          <p className="error">{operation.lastErrorMessage || operation.lastRemoteMessage}</p>
        )}
        <Btn width="210px" color="flex" isDisabled={isCheckingStatus} handleClick={() => dispatch(getGumrukAgcEntryStatus(operationId))}>
          {isCheckingStatus ? "Kontrol ediliyor..." : "Durumu tekrar kontrol et"}
        </Btn>
      </div>
    );
  };

  return (
    <ModalContainer role="dialog" aria-modal="true" aria-labelledby="gumruk-agc-title">
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <h2 id="gumruk-agc-title">Serbest Bölge Giriş Bildirimi</h2>
        {renderContent()}
        {errorMessage && <p className="error">{errorMessage}</p>}
      </div>
    </ModalContainer>
  );
};

export default GumrukAgcModal;
